import React, { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Decision } from "../../api/access/decisions";
import { requirementOf } from "../feedback/requirement";
import AnchoredTooltip from "../overlays/AnchoredTooltip";
import { useAnchoredTooltip } from "../overlays/useAnchoredTooltip";
import { buttonClassName, type ButtonSize, type ButtonVariant } from "../styles/buttonStyles";
import { classNames } from "../styles/classNames";
import LockedButton from "./LockedButton";

interface AppButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  /**
   * Why the action can't be taken here and now (P-B, F107). The button stays visible and
   * focusable, looks unavailable, does nothing when pressed, and says why on hover and on
   * focus, and to screen readers through `aria-describedby`.
   */
  unavailable?: ReactNode;
  /**
   * The served decision the action needs (SOT-02, accounts.md GS-8): refused about where things
   * are, the button isn't shown; about the person or what must happen first, it is unavailable
   * with why, and a permission one lacks opens BlockedState with Ask.
   */
  requires?: Decision;
}

export default function AppButton({ requires, ...props }: AppButtonProps) {
  return requires === undefined || requires.refusal === null ? <PlainAppButton {...props} /> : <RefusedAppButton decision={requires} {...props} />;
}

function RefusedAppButton({ decision, variant = "secondary", size = "md", fullWidth = false, className, ...props }: Omit<AppButtonProps, "requires"> & { decision: Decision }) {
  const { t } = useTranslation();
  const requirement = requirementOf(t, decision);
  if (!requirement.shown) return null;
  if (requirement.blocked !== null) {
    return <LockedButton decision={requirement.blocked} className={classNames(buttonClassName(variant, size), fullWidth && "w-full", className)} {...props} />;
  }
  return <PlainAppButton variant={variant} size={size} fullWidth={fullWidth} className={className} {...props} unavailable={requirement.reason} />;
}

function PlainAppButton({
  variant = "secondary",
  size = "md",
  fullWidth = false,
  className,
  type = "button",
  unavailable,
  ...props
}: Omit<AppButtonProps, "requires">) {
  const look = classNames(buttonClassName(variant, size), fullWidth && "w-full", className);
  if (unavailable !== undefined && unavailable !== null && unavailable !== false) {
    return <UnavailableButton type={type} className={look} reason={unavailable} {...props} />;
  }
  return <button type={type} className={look} {...props} />;
}

/** An unavailable action with its reason, through the shared anchored tooltip (`useAnchoredTooltip`). */
// A caller's `disabled` is dropped too: natively disabled, it couldn't take focus to say why (W-22, Sol r1 S2).
function UnavailableButton({ reason, onClick: _ignored, disabled: _focusable, "aria-describedby": describedBy, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { reason: ReactNode }) {
  const reasonId = useId();
  const tooltip = useAnchoredTooltip();
  return (
    <>
      <button
        {...props}
        aria-disabled="true"
        // A press does nothing, a form's submit included.
        onClick={(event) => { event.preventDefault(); }}
        aria-describedby={describedBy === undefined ? reasonId : `${describedBy} ${reasonId}`}
        {...tooltip.hoverProps()}
        {...tooltip.focusProps()}
      />
      <span id={reasonId} className="sr-only">{reason}</span>
      {tooltip.shown === null ? null : <AnchoredTooltip anchor={tooltip.shown.anchor}>{reason}</AnchoredTooltip>}
    </>
  );
}
